import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CTASection from './components/CTASection';
import { CONTACT } from 'config/contact';

const CaseStudies = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const industries = [
    {
      name: 'E-commerce',
      icon: 'ShoppingBag',
      focus: ['Store and warehouse sync', 'Abandoned cart recovery', 'Order and delivery updates'],
    },
    {
      name: 'Real Estate',
      icon: 'Building2',
      focus: ['Instant lead follow-up', 'Viewing scheduling', 'CRM updates without manual entry'],
    },
    {
      name: 'Healthcare',
      icon: 'HeartPulse',
      focus: ['Appointment reminders', 'Digital intake forms', 'Patient communication'],
    },
    {
      name: 'SaaS',
      icon: 'Cloud',
      focus: ['User onboarding flows', 'Support automation', 'Billing and CRM sync'],
    },
    {
      name: 'Agencies & Consultants',
      icon: 'Briefcase',
      focus: ['Client onboarding', 'Automated reporting', 'Lead nurturing sequences'],
    },
    {
      name: 'Local Services',
      icon: 'MapPin',
      focus: ['Bookings and reminders', 'Review generation', 'Local search visibility'],
    },
  ];

  const models = [
    {
      name: 'Done-For-You Build',
      description: 'We design, build, and launch the system, then hand it over with documentation.',
    },
    {
      name: 'Monthly Growth Retainer',
      description: 'Ongoing optimization, content, and campaign management with monthly reporting.',
    },
    {
      name: 'Audit & Roadmap',
      description: 'A one-time deep audit of your tools and processes, with a prioritized action plan.',
    },
  ];

  const steps = [
    { title: 'Discovery', description: 'We map your current process and find the repetitive work worth automating first.' },
    { title: 'Design', description: 'We design the workflow, pages, or campaigns around your tools and your customers.' },
    { title: 'Build', description: 'We build, connect, and test everything before it touches your customers.' },
    { title: 'Launch & Improve', description: 'We launch, measure, and keep optimizing from real results.' },
  ];

  return (
    <>
      <Helmet>
        <title>Client Work & Industries | Make Mind Automations</title>
        <meta
          name="description"
          content="The industries we serve and the automation and marketing systems we build for them — from e-commerce to local services."
        />
        <link rel="canonical" href="https://www.makemindautomations.tech/case-studies" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Client Work & Industries | Make Mind Automations" />
        <meta property="og:description" content="The industries we serve and the automation and marketing systems we build for them — from e-commerce to local services." />
        <meta property="og:url" content="https://www.makemindautomations.tech/case-studies" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main>
          <section className="py-20 lg:py-28 bg-background">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-3xl">
                <div className="inline-flex items-center space-x-2 px-3 py-1 bg-accent/10 border border-accent/20 rounded-full mb-6">
                  <Icon name="Award" size={14} className="text-accent" />
                  <span className="text-xs font-semibold text-accent uppercase tracking-wider">Our Work</span>
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-text-primary tracking-tight mb-6">
                  Client Work by Industry
                </h1>
                <p className="text-lg text-text-secondary leading-relaxed mb-10">
                  We build automation and marketing systems for businesses that want their time back.
                  Below are the industries we serve most, the work we do for each, and how engagements
                  are structured. Detailed client stories are added here as clients approve them for
                  publication.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/contact">
                    <Button variant="default" size="lg" iconName="Calendar" iconPosition="left">
                      Book Free Audit
                    </Button>
                  </Link>
                  <a href={CONTACT.whatsappLink} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="lg" iconName="MessageCircle" iconPosition="left">
                      Chat on WhatsApp
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </section>

          <section className="py-20 bg-surface">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-12">Industries we serve</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {industries.map((industry) => (
                  <div key={industry.name} className="bg-card border border-border rounded-2xl p-8">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                      <Icon name={industry.icon} size={24} className="text-primary" />
                    </div>
                    <h3 className="text-xl font-bold text-text-primary mb-4">{industry.name}</h3>
                    <ul className="space-y-2.5">
                      {industry.focus.map((item) => (
                        <li key={item} className="flex items-start space-x-2.5 text-sm text-text-secondary">
                          <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-20 bg-background">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-12">How engagements work</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {models.map((model) => (
                  <div key={model.name} className="bg-card border border-border rounded-2xl p-8">
                    <h3 className="text-xl font-bold text-text-primary mb-2">{model.name}</h3>
                    <p className="text-sm text-text-secondary leading-relaxed">{model.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-20 bg-surface">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-12">Our process</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {steps.map((step, index) => (
                  <div key={step.title} className="bg-card border border-border rounded-2xl p-6">
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                      <span className="text-primary font-bold">{index + 1}</span>
                    </div>
                    <h3 className="text-lg font-bold text-text-primary mb-2">{step.title}</h3>
                    <p className="text-sm text-text-secondary leading-relaxed">{step.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <CTASection />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default CaseStudies;
